import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Col,
  DatePicker,
  DateRangePicker,
  Form,
  Row,
  Tooltip,
  Whisper,
} from "rsuite";
import HelpOutlineIcon from "@rsuite/icons/HelpOutline";
import TimeIcon from "@rsuite/icons/Time";
import CloseIcon from "@rsuite/icons/Close";
import TagPicker from "../../components/common/tagPicker/TagPicker";
import usePropertyPickerOptions from "../../hooks/usePropertyPickerOptions";
import { formatDateRange } from "../../utils/dateUtils";
import UserTypeahead from "./UserTypeahead";
import {
  INIT_FORM_VALUE,
  getMissingSearchGroups,
  getSearchValidationError,
} from "./utils";

const hotelCodeHelp = (
  <Tooltip>
    Enter a property code to filter activity for a single hotel.
  </Tooltip>
);

const UserActivityForm = ({
  properties,
  formValue,
  setFormValue,
  onSearch,
  source,
  setIsDrawerOpen,
}) => {
  // The Hotel Name picker is server-side searchable: the property store only holds
  // the first 20 hotels (it backs the header selector, which searches by name via
  // the API), so binding the picker to that list would hide every other hotel. This
  // hook fetches a page per keyword and keeps selected tags resolved to a label.
  const propertyToOption = useCallback((property) => property, []);
  const getPropertyValue = useCallback((property) => property?.propertyId, []);
  // Seed with the store list AND anything already picked, so re-opening Edit search
  // renders the chosen hotels as labelled tags even when they're beyond the store's
  // first page. Memoized so the hook's seed effect doesn't run on every render.
  const seedProperties = useMemo(
    () => [...properties, ...(formValue.hotelProperties || [])],
    [properties, formValue.hotelProperties]
  );
  const {
    options: propertyOptions,
    onSearch: onPropertiesSearch,
    reset: resetPropertyOptions,
  } = usePropertyPickerOptions({
    selectedValues: formValue.hotelIds,
    toOption: propertyToOption,
    getOptionValue: getPropertyValue,
    seedProperties,
  });

  const handleChange = (key, value) => {
    setFormValue((prev) => ({ ...prev, [key]: value }));
  };

  // Keep the selected property objects alongside their ids: the summary chip and a
  // re-opened picker need names, and a server-searched hotel isn't in the store list.
  const handleHotelsChange = (ids) => {
    const known = new Map(
      [...propertyOptions, ...(formValue.hotelProperties || [])].map((p) => [
        p.propertyId,
        p,
      ])
    );
    const hotelProperties = (ids || [])
      .map((id) => known.get(id))
      .filter(Boolean);
    setFormValue((prev) => ({ ...prev, hotelIds: ids, hotelProperties }));
  };

  // Which mandatory groups the last submit was missing. Set on submit, cleared as soon
  // as the user supplies the value — same contract as the Price Check form.
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setValidationErrors((prev) =>
      Object.keys(prev).length ? getMissingSearchGroups(formValue) : prev
    );
  }, [formValue]);

  const handleSubmit = () => {
    // A hotel is required, satisfied by either Name or Code, so both labels carry a
    // * (EO-1835). The toast names what is missing and both fields turn red.
    // Surface the reason instead of a dead button: a blank search silently doing
    // nothing read as a broken Search (EO-1454).
    const validationError = getSearchValidationError(formValue);
    if (validationError) {
      toast.error(validationError);
      setValidationErrors(getMissingSearchGroups(formValue));
      return;
    }
    setValidationErrors({});
    onSearch(formValue);
  };

  // Either field satisfies the hotel group, so both are flagged when it's missing.
  const hotelErrorClass = validationErrors.hotel ? "validation-error" : "";

  const isDrawer = source === "drawer";

  return (
    <div
      className={`user-activity-form${isDrawer ? " user-activity-form--drawer" : ""}`}
    >
      {isDrawer && (
        <div className="user-activity-form__drawer-header">
          {/* As the Reservation Report Edit Search drawer: the page title. */}
          <p className="user-activity-form__drawer-title">User Activity</p>
          <Button
            appearance="ghost"
            endIcon={<CloseIcon />}
            onClick={() => setIsDrawerOpen(false)}
          >
            Close
          </Button>
        </div>
      )}
      <Form fluid formValue={formValue}>
        <Row>
          <Col xs={5}>
            <Form.Group controlId="hotelIds">
              <Form.ControlLabel>
                Hotel Name <span className="required-field">*</span>
              </Form.ControlLabel>
              <TagPicker
                block
                data={propertyOptions}
                labelKey="propertyName"
                valueKey="propertyId"
                placeholder="Select hotels"
                value={formValue.hotelIds}
                onChange={handleHotelsChange}
                onSearch={onPropertiesSearch}
                onSelect={resetPropertyOptions}
                className={hotelErrorClass}
              />
            </Form.Group>
          </Col>
          <Col xs={5} xsPush={1}>
            <Form.Group controlId="hotelCode">
              <Form.ControlLabel>
                Hotel Code <span className="required-field">*</span>
                <Whisper placement="top" speaker={hotelCodeHelp}>
                  <HelpOutlineIcon className="user-activity-form__help-icon" />
                </Whisper>
              </Form.ControlLabel>
              <Form.Control
                name="hotelCode"
                placeholder="Enter hotel code"
                value={formValue.hotelCode}
                onChange={(val) => handleChange("hotelCode", val)}
                className={hotelErrorClass}
              />
            </Form.Group>
          </Col>
          <Col xs={5} xsPush={2}>
            <Form.Group controlId="userName">
              <Form.ControlLabel>User Name</Form.ControlLabel>
              <UserTypeahead
                displayBy="name"
                placeholder="Enter name"
                value={formValue.userName}
                onChange={(val) => handleChange("userName", val)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={5}>
            <Form.Group controlId="userEmail">
              <Form.ControlLabel>User Email</Form.ControlLabel>
              <UserTypeahead
                displayBy="email"
                placeholder="Enter email"
                value={formValue.userEmail}
                onChange={(val) => handleChange("userEmail", val)}
              />
            </Form.Group>
          </Col>
          <Col xs={5} xsPush={1}>
            <Form.Group controlId="actionDate">
              <Form.ControlLabel>Action Date</Form.ControlLabel>
              <DateRangePicker
                block
                value={formValue.actionDate}
                placeholder="Choose dates"
                format="MMM d, yyyy"
                renderValue={formatDateRange}
                onChange={(val) => handleChange("actionDate", val)}
                container={() => document.body}
                placement="autoVerticalStart"
              />
            </Form.Group>
          </Col>
          <Col xs={5} xsPush={2}>
            <Form.Group controlId="actionTime">
              <Form.ControlLabel>Action Time</Form.ControlLabel>
              <DatePicker
                block
                value={formValue.actionTime}
                placeholder="Choose time"
                format="HH:mm"
                caretAs={TimeIcon}
                onChange={(val) => handleChange("actionTime", val)}
                container={() => document.body}
                placement="autoVerticalStart"
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="user-activity-form__actions">
          <Button
            appearance="ghost"
            onClick={() => setFormValue(INIT_FORM_VALUE)}
          >
            Reset
          </Button>
          <Button appearance="primary" onClick={handleSubmit}>
            Search
          </Button>
        </div>
      </Form>
    </div>
  );
};

UserActivityForm.propTypes = {
  properties: PropTypes.array,
  formValue: PropTypes.object.isRequired,
  setFormValue: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  source: PropTypes.string,
  setIsDrawerOpen: PropTypes.func,
};

UserActivityForm.defaultProps = {
  properties: [],
  source: "",
  setIsDrawerOpen: () => {},
};

export default UserActivityForm;
